import React, { useEffect, useMemo, useState } from "react";
import {
  isAfter,
  isBefore,
  addDays
} from "date-fns";
import {
  Calendar,
  Clock,
  Search,
  Tag,
  Download,
  AlertCircle,
  Filter,
  RefreshCw,
  Grid3X3,
  List,
  Star
} from "lucide-react";
import type { EventRow } from "../types/event";
import { RAW_DATA_URL } from "../config";
import { resolveDataUrl, safeReplaceQuery } from "../lib/url";
import { parseTags, pushGA } from "../lib/utils";
import { toICSBatch } from "../lib/ics";
import { fetchEvents } from "../services/fetchEvents";
import Badge from "../components/Badge";
import SectionTitle from "../components/SectionTitle";
import CardsSkeleton from "../components/CardsSkeleton";
import EmptyState from "../components/EmptyState";
import EventCard from "../components/EventCard";
import sampleData from "../data/sampleData";

export default function AgendaPage() {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useSample, setUseSample] = useState(false);

  // filtros + URL sync
  const [q, setQ] = useState("");
  const [tag, setTag] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Ler q e tag da URL
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const q0 = sp.get('q') || '';
      const t0 = sp.get('tag');
      setQ(q0);
      setTag(t0);
    } catch {
      // ignora
    }
  }, []);

  // Persistir q e tag na URL
  useEffect(() => {
    safeReplaceQuery({ q, tag });
  }, [q, tag]);

  // Fetch com retry/backoff
  const doFetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const resolvedUrl = resolveDataUrl(RAW_DATA_URL);
      const rows = await fetchEvents(resolvedUrl);
      setEvents(rows);
      setUseSample(false);
    } catch (e: any) {
      setError((e?.message || 'Falha ao buscar eventos'));
      setEvents(sampleData);
      setUseSample(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { doFetch(); }, []);

  const now = new Date();

  const filtered = useMemo(() => {
    const search = q.trim().toLowerCase();
    return events.filter((ev) => {
      if (tag && !parseTags(ev.tags).includes(tag)) return false;
      if (search) {
        const txt = `${ev.title} ${ev.description} ${ev.speakers} ${ev.tags}`.toLowerCase();
        if (!txt.includes(search)) return false;
      }
      return true;
    });
  }, [events, q, tag]);

  const { upcoming, past } = useMemo(() => {
    const up: EventRow[] = [];
    const pa: EventRow[] = [];
    filtered.forEach((ev) => {
      const start = new Date(ev.start_iso);
      if (isNaN(+start)) return pa.push(ev);
      if (isAfter(start, now)) up.push(ev); else pa.push(ev);
    });
    up.sort((a, b) => +new Date(a.start_iso) - +new Date(b.start_iso));
    pa.sort((a, b) => +new Date(b.start_iso) - +new Date(a.start_iso));
    return { upcoming: up, past: pa };
  }, [filtered, now]);

  const highlightedUpcoming = useMemo(() => 
    upcoming.filter((ev) => (ev.destaque || "").toLowerCase() === "sim"), 
    [upcoming]
  );

  const regularUpcoming = useMemo(() => 
    upcoming.filter((ev) => (ev.destaque || "").toLowerCase() !== "sim"), 
    [upcoming]
  );

  const allTags = useMemo(() => {
    const set = new Set<string>();
    events.forEach((e) => parseTags(e.tags).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [events]);

  // Exportar todos .ics dos próximos 30 dias
  const handleExportAll = () => {
    const in30 = addDays(now, 30);
    const slice = upcoming.filter(ev => {
      const d = new Date(ev.start_iso);
      return isBefore(d, in30);
    });
    const ics = toICSBatch(slice);
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `agenda-proximos-30dias.ics`;
    a.click();
    URL.revokeObjectURL(url);
    pushGA('download_ics', { scope: 'batch_30days', count: slice.length });
  };

  return (
    <>
      {/* Hero Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Header Content */}
        <div className="py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full bg-darling-lilac/10 dark:bg-darling-lilac/20 px-4 py-2 mb-6">
              <Calendar className="h-4 w-4 text-darling-lilac" />
              <span className="text-sm font-exo font-medium text-darling-lilac">
                Agenda Oficial
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-exo font-black text-soft-black dark:text-white mb-6 leading-tight">
              <span className="bg-gradient-to-r from-darling-lilac via-deep-purple to-ocean-water bg-clip-text text-transparent">
                Embaixadores
              </span>
              <br />
              <span className="text-soft-black dark:text-white">
                CRO Brasil
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-soft-black/70 dark:text-light-gray/80 font-exo font-light leading-relaxed max-w-3xl mx-auto mb-8">
              Próximos encontros, links de participação, adicionar ao Google Agenda e acesso às gravações da nossa comunidade de embaixadores.
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-soft-black/60 dark:text-light-gray/90 font-exo">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-darling-lilac"></div>
                <span>Eventos ao vivo</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-ocean-water"></div>
                <span>Gravações disponíveis</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-sky"></div>
                <span>Integração com calendários</span>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="pb-8">
          <div className="rounded-2xl border border-light-gray-alt dark:border-deep-purple/30 bg-white/80 dark:bg-soft-black/80 backdrop-blur-sm px-6 py-6 shadow-lg">
            <div className="flex flex-col gap-6">
              {/* Search Bar */}
              <div className="flex items-center gap-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-darling-lilac/20 focus-within:border-darling-lilac/50 transition-all">
                <Search className="h-5 w-5 text-darling-lilac flex-shrink-0" />
                <input
                  className="w-full bg-transparent outline-none text-sm font-exo placeholder:text-soft-black/50 dark:placeholder:text-light-gray/50 text-soft-black dark:text-white"
                  placeholder="Buscar por título, tema ou palestrante..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onBlur={() => pushGA('filter_apply', { q, tag })}
                />
              </div>
              
              {/* Filter Tags */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-soft-black/70 dark:text-light-gray/90 font-exo font-medium">
                  <Filter className="h-4 w-4"/>
                  <span>Filtros por categoria</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    className={`px-4 py-2 rounded-lg text-sm font-exo font-medium border transition-all duration-200 ${
                      !tag 
                        ? "bg-darling-lilac text-white border-darling-lilac shadow-md hover:shadow-lg" 
                        : "bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 text-soft-black dark:text-white border-light-gray-alt dark:border-deep-purple/50"
                    }`}
                    onClick={() => { setTag(null); pushGA('filter_apply', { q, tag: null }); }}
                  >
                    Todos os eventos
                  </button>
                  {allTags.map((t) => (
                    <button 
                      key={t} 
                      className={`px-4 py-2 rounded-lg text-sm font-exo font-medium border transition-all duration-200 ${
                        tag === t 
                          ? "bg-darling-lilac text-white border-darling-lilac shadow-md hover:shadow-lg" 
                          : "bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 text-soft-black dark:text-white border-light-gray-alt dark:border-deep-purple/50"
                      }`} 
                      onClick={() => { setTag(t); pushGA('filter_apply', { q, tag: t }); }}
                    >
                      <div className="flex items-center gap-1.5">
                        <Tag className="h-3.5 w-3.5" />
                        {t}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-light-gray-alt dark:border-deep-purple/30">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-1 rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded text-xs transition-all ${
                        viewMode === 'grid'
                          ? 'bg-darling-lilac text-white'
                          : 'text-soft-black/60 dark:text-light-gray/60 hover:text-soft-black dark:hover:text-white'
                      }`}
                      title="Visualização em grade"
                    >
                      <Grid3X3 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-1.5 rounded text-xs transition-all ${
                        viewMode === 'list'
                          ? 'bg-darling-lilac text-white'
                          : 'text-soft-black/60 dark:text-light-gray/60 hover:text-soft-black dark:hover:text-white'
                      }`}
                      title="Visualização em lista"
                    >
                      <List className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  
                  {useSample && (
                    <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30 dark:bg-sun-flower/10 dark:text-sun-flower dark:border-sun-flower/20">
                      <AlertCircle className="h-3 w-3" />
                      Dados de exemplo
                    </Badge>
                  )}
                  {!useSample && (
                    <Badge className="bg-ocean-water/20 text-ocean-water border-ocean-water/30 dark:bg-ocean-water/10 dark:text-ocean-water dark:border-ocean-water/20">
                      <div className="w-2 h-2 rounded-full bg-ocean-water animate-pulse" />
                      Dados atualizados
                    </Badge>
                  )}
                  {error && (
                    <span className="inline-flex items-center gap-1 text-sun-flower dark:text-sun-flower font-exo">
                      <AlertCircle className="h-3.5 w-3.5"/> 
                      {error}
                    </span>
                  )}
                </div>
                <button
                  onClick={doFetch} 
                  className="inline-flex items-center gap-2 rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 hover:bg-light-gray dark:hover:bg-deep-purple/40 px-3 py-1.5 text-xs font-exo font-medium text-soft-black dark:text-white transition-all duration-200"
                >
                  <RefreshCw className="h-3.5 w-3.5"/> 
                  Atualizar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* PRÓXIMOS DESTACADOS */}
        {highlightedUpcoming.length > 0 && !loading && (
          <section className="mb-12">
            <div className="flex items-center gap-3 text-soft-black dark:text-white mb-6">
              <Star className="h-5 w-5 text-sun-flower fill-current" />
              <h2 className="text-xl md:text-2xl font-exo font-bold tracking-wide">Eventos em destaque</h2>
            </div>
            <div className={`${viewMode === 'grid' ? 'grid sm:grid-cols-2 xl:grid-cols-3 gap-8' : 'space-y-4'}`}>
              {highlightedUpcoming.map((ev) => (
                <EventCard key={ev.id} ev={ev} past={false} viewMode={viewMode} />
              ))}
            </div>
          </section>
        )}

        {/* PRÓXIMOS REGULARES */}
        <SectionTitle icon={Calendar}>
          {highlightedUpcoming.length > 0 ? 'Outros eventos próximos' : 'Próximos eventos'}
        </SectionTitle>
        {loading ? (
          <CardsSkeleton />
        ) : (
          <>
            <div className={`mt-8 ${viewMode === 'grid' ? 'grid sm:grid-cols-2 xl:grid-cols-3 gap-8' : 'space-y-4'}`}>
              {regularUpcoming.map((ev) => (
                <EventCard key={ev.id} ev={ev} past={false} viewMode={viewMode} />
              ))}
            </div>
            {!loading && regularUpcoming.length === 0 && highlightedUpcoming.length === 0 && (
              <EmptyState title="Sem eventos futuros">
                Aguarde! Em breve novos encontros aparecerão aqui.
              </EmptyState>
            )}
          </>
        )}

        {/* PASSADOS */}
        <div className="mt-20" />
        <SectionTitle icon={Clock}>Eventos passados</SectionTitle>
        {loading ? (
          <CardsSkeleton />
        ) : (
          <div className={`mt-8 ${viewMode === 'grid' ? 'grid sm:grid-cols-2 xl:grid-cols-3 gap-8' : 'space-y-4'}`}>
            {past.map((ev) => (
              <EventCard key={ev.id} ev={ev} past viewMode={viewMode} />
            ))}
          </div>
        )}
      </main>
    </>
  );
}